import CustomTextInput from "../../../components/Input/Text/TextInput";
import classes from "./Personal.module.css";
import CustomSelecter from "../../../components/Input/Autocomplete/Autocomplete";
import locationIcon from "../../../assets/location.svg";
import emailIcon from "../../../assets/email.svg";
import CustomRadioButton from "../../../components/Input/RadioButton/RadioButton";
import {
  getGenderOptions,
  getExtraQuestionOptions,
  getInformationSourceOptions,
  getWorkTimeOptions,
  getNationalityOptions,
} from "../../../utils/translatedOptions";
import DateInputDemo from "../../../components/Input/Date/Date";
import CustomPhoneInput from "../../../components/Input/Phone/PhoneInput";
import FileUpload from "../../../components/UploadPic/UploadPic";
import { useState } from "react";
import arrow from "../../../assets/arrow_from_to.svg";
import { useTranslation } from "../../../I18n/Languages/LanguageContext";

export default function PersonalForm({
  formData,
  updateFormData,
  setFormData,
  jobOptions,
  uploadCVFile,
  uploadCoverLetterFile,
  uploadPhotoFile,
}) {
  const { t } = useTranslation();
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [salaryMinError, setSalaryMinError] = useState("");
  const [salaryMaxError, setSalaryMaxError] = useState("");
  const [birthDateError, setBirthDateError] = useState("");
  const [startDateError, setStartDateError] = useState("");
  const validateEmail = (email) => {
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    setEmailError(isValid ? "" : t("email_must_contain_at"));
    return isValid;
  };

  const handlePhoneChange = (e) => {
    let phoneValue = e.target.value;

    const onlyNumbers = phoneValue.replace(/\D/g, "");

    if (onlyNumbers.length < 9) {
      setPhoneError(t("phone_minimum_digits"));
    } else {
      setPhoneError("");
    }

    updateFormData("telefon", onlyNumbers);
  };

  const validateAge = (birthDate) => {
    if (!birthDate) {
      setBirthDateError("");
      return false;
    }

    const today = new Date();
    const birth = new Date(birthDate);

    if (birth > today) {
      setBirthDateError("Birth date cannot be in the future.");
      return false;
    }

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }

    if (age < 18) {
      setBirthDateError("You must be at least 18 years old to apply.");
      return false;
    }

    setBirthDateError("");
    return true;
  };

  const handleBirthDateChange = (e) => {
    const selectedDate = e.target.value;
    validateAge(selectedDate);
    updateFormData("datePersonal", selectedDate);
  };

  const validateStartDate = (startDate) => {
    if (!startDate) {
      setStartDateError("");
      return true;
    }

    const today = new Date();
    const start = new Date(startDate);

    if (start < today.setHours(0, 0, 0, 0)) {
      setStartDateError("Start date cannot be in the past.");
      return false;
    }

    setStartDateError("");
    return true;
  };

  const handleStartDateChange = (e) => {
    const selectedDate = e.target.value;
    validateStartDate(selectedDate);
    updateFormData("startDateAvailable", selectedDate);
  };

  const validateSalaryRange = (minValue, maxValue) => {
    const min = parseFloat(minValue) || 0;
    const max = parseFloat(maxValue) || 0;

    setSalaryMinError("");
    setSalaryMaxError("");

    if (minValue && maxValue) {
      if (min > max) {
        setSalaryMinError(
          "Minimum salary cannot be greater than maximum salary"
        );
        setSalaryMaxError("Maximum salary cannot be less than minimum salary");
        return false;
      }
    }
    return true;
  };

  const handleMinSalaryChange = (e) => {
    const newMinValue = e.target.value;
    updateFormData("salaryRangeExpectationMinimum", newMinValue);
    validateSalaryRange(newMinValue, formData.salaryRangeExpectationMaximum);
  };

  const handleMaxSalaryChange = (e) => {
    const newMaxValue = e.target.value;
    updateFormData("salaryRangeExpectationMaximum", newMaxValue);
    validateSalaryRange(formData.salaryRangeExpectationMinimum, newMaxValue);
  };

  const getMaxBirthDate = () => {
    const today = new Date();
    const maxDate = new Date(
      today.getFullYear() - 18,
      today.getMonth(),
      today.getDate()
    );
    return maxDate.toISOString().split("T")[0];
  };

  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  return (
    <div className={classes.personalForm}>
      <div className={classes.firstGroup}>
        <CustomTextInput
          name={t("name_surname")}
          label={t("full_name")}
          topLabel={t("full_name")}
          value={formData.fullName}
          maxLength={100}
          onChange={(e) => {
            const fullName = e.target.value;

            const nameParts = fullName
              .trim()
              .split(/\s+/)
              .filter((part) => part.length > 0);

            let firstName = "";
            let lastName = "";

            if (nameParts.length > 0) {
              firstName = nameParts[0];

              if (nameParts.length > 1) {
                lastName = nameParts.slice(1).join(" ");
              }
            }

            setFormData((prev) => ({
              ...prev,
              fullName,
              firstName,
              lastName,
            }));
          }}
        />
        <div style={{ width: "100%" }}>
          <CustomSelecter
            width="100%"
            options={getGenderOptions(t)}
            label={t("gender")}
            name="select"
            topLabel={t("gender")}
            value={formData.gjinia}
            onChange={(value) => updateFormData("gjinia", value)}
          />
        </div>
      </div>
      <div className={classes.firstGroup}>
        <DateInputDemo
          onChange={handleBirthDateChange}
          value={formData.datePersonal}
          topLabel={t("date_of_birth")}
          error={!!birthDateError}
          helperText={birthDateError}
          max={getMaxBirthDate()}
          min="1900-01-01"
        />
        <CustomSelecter
          width="100%"
          options={getNationalityOptions(t)}
          label={t("select_nationality")}
          name="nationality"
          topLabel={t("nationality")}
          value={formData.nationality}
          onChange={(value) => updateFormData("nationality", value)}
        />
      </div>
      <div className={classes.firstGroup}>
        <CustomPhoneInput
          topLabel={t("contact_number")}
          name="telefon"
          onChange={handlePhoneChange}
          value={formData.telefon}
          error={!!phoneError}
          helperText={phoneError}
        />
        <CustomTextInput
          name="name"
          label={t("email_address")}
          topLabel={t("email_address")}
          value={formData.email}
          maxLength={254}
          onChange={(e) => {
            const newEmail = e.target.value;
            updateFormData("email", newEmail);
            validateEmail(newEmail);
          }}
          icon={<img src={emailIcon} alt="email" />}
          iconPosition="end"
          error={!!emailError}
          helperText={emailError}
        />
      </div>
      <div className={classes.firstGroup}>
        <CustomTextInput
          width="100%"
          name="name"
          label={t("address_of_residence")}
          topLabel={t("address_of_residence")}
          value={formData.adresaPersonal}
          maxLength={200}
          onChange={(e) => updateFormData("adresaPersonal", e.target.value)}
          icon={
            <img src={locationIcon} alt="location" className={classes.icon} />
          }
          iconPosition="end"
        />
        <div style={{ width: "100%" }}>
          {" "}
          <FileUpload
            onFileSelect={(file) => {
              updateFormData("photoFile", file);
              if (file) {
                uploadPhotoFile(file);
              }
            }}
            label={t("photo")}
            topLabel={t("upload_photo")}
            acceptedTypes="image/"
            maxSize={2 * 1024 * 1024}
            coverLetter={true}
            value={formData.photoFile}
          />
        </div>
      </div>
      <div className={classes.divider} />
      <h3 className={classes.h3redTitle}>{t("position_details")}</h3>
      <div className={classes.firstGroup}>
        <CustomSelecter
          width="100%"
          options={jobOptions}
          value={formData.pozicioni}
          name="pozicioni"
          topLabel={t("position_applied_for_label")}
          label={t("select_position")}
          onChange={(value) => updateFormData("pozicioni", value)}
        />
        <CustomRadioButton
          name="mundësiaPunë"
          options={getWorkTimeOptions(t)}
          value={formData.mundësiaPunë}
          onChange={(e) => updateFormData("mundësiaPunë", e.target.value)}
          topLabel={t("employment_type_desired")}
        />
      </div>
      <div className={classes.firstGroup}>
        <DateInputDemo
          value={formData.startDateAvailable}
          onChange={handleStartDateChange}
          topLabel={t("earliest_start_date")}
          error={!!startDateError}
          helperText={startDateError}
          min={getTodayDate()}
          max={undefined}
          width="100%"
        />
        <div className={classes.dateGroup}>
          <CustomTextInput
            topLabel={t("salary_range_expectation")}
            width="100%"
            label={t("from")}
            isNumber={true}
            name="salaryRangeExpectationMinimum"
            maxLength={25}
            value={formData.salaryRangeExpectationMinimum}
            onChange={handleMinSalaryChange}
            error={!!salaryMinError}
            helperText={salaryMinError}
          />
          <img alt="arrow" className={classes.dateArrow} src={arrow} />
          <CustomTextInput
            name="salaryRangeExpectationMaximum"
            width="100%"
            maxLength={25}
            value={formData.salaryRangeExpectationMaximum}
            label={t("to")}
            isNumber={true}
            onChange={handleMaxSalaryChange}
            error={!!salaryMaxError}
            helperText={salaryMaxError}
          />
        </div>
      </div>
      <div className={classes.divider} />
      <h3 className={classes.h3redTitle}>{t("attachments")}</h3>
      <div className={classes.firstGroup}>
        <FileUpload
          onFileSelect={(file) => {
            updateFormData("cvFile", file);
            if (file) {
              uploadCVFile(file);
            }
          }}
          label={t("upload_cv")}
          acceptedTypes="application/pdf"
          topLabel={t("upload_cv_description")}
          maxSize={2 * 1024 * 1024}
          coverLetter={true}
          value={formData.cvFile}
        />
        <FileUpload
          onFileSelect={(file) => {
            updateFormData("coverLetterFile", file);
            if (file) {
              uploadCoverLetterFile(file);
            }
          }}
          label={t("motivation_letter")}
          topLabel={t("upload_motivation_letter")}
          acceptedTypes="application/pdf"
          maxSize={2 * 1024 * 1024}
          coverLetter={true}
          value={formData.coverLetterFile}
        />
      </div>
      <div className={classes.divider} />
      <h3 className={classes.h3redTitle}>{t("additional_information")}</h3>
      <CustomRadioButton
        name="additionalQuestions"
        topLabel={t("security_background_check")}
        options={getExtraQuestionOptions(t)}
        value={formData.securityBackgroundChecks}
        onChange={(e) =>
          updateFormData("securityBackgroundChecks", e.target.value)
        }
      />
      <CustomRadioButton
        name="additionalQuestions"
        topLabel={t("physical_fitness")}
        options={getExtraQuestionOptions(t)}
        value={formData.physicalFitRequirement}
        onChange={(e) =>
          updateFormData("physicalFitRequirement", e.target.value)
        }
      />
      <CustomRadioButton
        name="additionalQuestions"
        topLabel={t("relatives_at_tia_detailed")}
        options={getExtraQuestionOptions(t)}
        value={formData.relativesWorking}
        onChange={(e) => updateFormData("relativesWorking", e.target.value)}
      />
      {formData.relativesWorking === 1 && (
        <div className={classes.firstGroup}>
          <CustomTextInput
            topLabel={t("name_surname")}
            label={t("name_surname")}
            value={formData.relativesWorkingNameSpecifications}
            maxLength={100}
            onChange={(e) =>
              updateFormData(
                "relativesWorkingNameSpecifications",
                e.target.value
              )
            }
          />
          <CustomTextInput
            topLabel={t("relationship")}
            label={t("relationship_placeholder")}
            value={formData.relativesWorkingRelationshipSpecifications}
            maxLength={100}
            onChange={(e) =>
              updateFormData(
                "relativesWorkingRelationshipSpecifications",
                e.target.value
              )
            }
          />
        </div>
      )}

      <CustomRadioButton
        name="jobApplicationNotice"
        topLabel={t("how_learned_position")}
        options={getInformationSourceOptions(t)}
        value={formData.jobApplicationNotice}
        onChange={(e) => updateFormData("jobApplicationNotice", e.target.value)}
        isFullWidth={true}
      />
      <CustomTextInput
        topLabel={t("why_applying_position")}
        label={t("reason_for_applying")}
        value={formData.reasonForApplying}
        onChange={(e) => updateFormData("reasonForApplying", e.target.value)}
      />
    </div>
  );
}
